import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check, UserRound, Briefcase } from "lucide-react";
import { toast } from "sonner";
import { api, useAuth } from "@/context/AuthContext";
import {
  LS_CONTEXT,
  SS_MATCH,
  PRACTICE_TYPES,
  PROSPECT_TYPES,
  DIFFICULTIES,
} from "./PracticeSetup";

/* Context Briefing — shown to BOTH matched users after matchmaking pairs
   them, before they enter the live call.

   The match payload (room + partner name + partner context) arrives in the
   matchmaking heartbeat response and is stashed in sessionStorage by the
   Matchmaking page, so a refresh on this screen loses nothing. The user's
   own context lives in localStorage (written by PracticeSetup).

   Roles are assigned by the server at pair time (fair rotation): the Rep
   opens the call, the Prospect listens. Presentation: the assigned
   prospect's scenario leads (left on desktop, stacked on top on mobile),
   the rep's follows — cards are name + role labelled for the current
   round and colour/icon coded (slate = prospect, red = rep). */

const PRACTICE_LABEL = Object.fromEntries(PRACTICE_TYPES.map((t) => [t.key, t.title]));
const PROSPECT_LABEL = Object.fromEntries(PROSPECT_TYPES.map((t) => [t.key, t.label]));
const DIFFICULTY_LABEL = Object.fromEntries(DIFFICULTIES.map((t) => [t.key, t.label]));

const initialsOf = (name = "?") => (name || "?").charAt(0).toUpperCase();

export function ScenarioCard({ testid, title, name, context, sellerLine, accent, icon: Icon, tone }) {
  const rows = [
    ["Practice", PRACTICE_LABEL[context?.practice_type] || "—"],
    ["Selling", context?.selling || "—"],
    ["Extra context", context?.additional_context || "—"],
    ["Prospect", PROSPECT_LABEL[context?.prospect_type] || "—"],
    ["Difficulty", DIFFICULTY_LABEL[context?.difficulty] || "—"],
  ];
  // Optional role theming ("rep" = red, "prospect" = cool slate) used by the
  // Briefing screen so the two roles are instantly distinguishable. When
  // `tone` is omitted the legacy accent/plain chrome applies unchanged, so
  // the in-call context panel keeps its exact current look.
  const borderCls =
    tone === "rep"
      ? "border-[#E52525]/40"
      : tone === "prospect"
        ? "border-slate-400/25"
        : accent
          ? "border-[#E52525]/40"
          : "border-white/10";
  const showBar = tone ? true : accent;
  const barCls = tone === "prospect" ? "bg-slate-400/60" : "bg-[#E52525]";
  const avatarCls =
    tone === "prospect"
      ? "bg-slate-400/10 text-slate-300"
      : "bg-[#E52525]/15 text-[#FF5A5A]";
  const iconCls = tone === "prospect" ? "text-slate-400" : "text-[#FF5A5A]";
  return (
    <motion.section
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45 }}
      data-testid={testid}
      className={`relative overflow-hidden rounded-3xl border bg-[#111111] p-6 sm:p-7 ${borderCls}`}
    >
      {showBar && (
        <span aria-hidden="true" className={`absolute inset-x-0 top-0 h-0.5 ${barCls}`} />
      )}
      <div className="flex items-center gap-3">
        <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold ${avatarCls}`}>
          {initialsOf(name)}
        </span>
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500">
            {Icon && <Icon size={12} className={iconCls} aria-hidden="true" />}
            {title}
          </p>
          <p className="truncate text-sm font-semibold text-white">{name}</p>
        </div>
      </div>

      <p className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-medium text-zinc-300">
        <UserRound size={12} className={iconCls} />
        {sellerLine}
      </p>

      <dl className="mt-5 space-y-3.5">
        {rows.map(([k, v]) => (
          <div key={k}>
            <dt className="text-[11px] font-semibold uppercase tracking-[0.12em] text-zinc-500">
              {k}
            </dt>
            <dd className="mt-1 whitespace-pre-wrap text-sm font-light leading-relaxed text-zinc-200">
              {v}
            </dd>
          </div>
        ))}
      </dl>
    </motion.section>
  );
}

export default function Briefing() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const payload = useMemo(() => {
    try {
      const raw = sessionStorage.getItem(SS_MATCH);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const myContext = useMemo(() => {
    try {
      const raw = localStorage.getItem(LS_CONTEXT);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const myName = user?.name || user?.username || "You";
  const partnerName = payload?.partner_name || "Your partner";

  /* ---- Synchronized preparation countdown -------------------------------
     Both users must explicitly tap "I'm Ready"; page load never counts.
     The server fixes a shared start_at the moment both have confirmed,
     both clients tick against that absolute target (RTT-corrected clock
     sync), and at zero handleEnter fires automatically. There is no early
     entry. Leaving or disconnecting before zero cancels for both sides. */
  const [phase, setPhase] = useState("confirm"); // confirm | countdown | cancelled
  const [cancelReason, setCancelReason] = useState(null); // "left" | "gone" | "expired"
  const [iAmReady, setIAmReady] = useState(false);
  const [partnerReady, setPartnerReady] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(null);
  const readyRef = useRef(false); // mirrors the explicit tap for the poll loop
  const offsetRef = useRef(0); // server_clock - local_clock (ms)
  const startAtRef = useRef(null); // server-epoch ms when the call auto-starts
  const enteredRef = useRef(false); // auto-enter fires exactly once

  const handleEnter = () => {
    navigate("/video-call", {
      replace: true,
      state: {
        mode: "matchmaking",
        room: payload.room,
        videoDeviceId: payload.videoDeviceId || "",
        audioDeviceId: payload.audioDeviceId || "",
        cameraOn: payload.cameraOn !== false,
        yourRole: payload.your_role || null, // server-assigned rep|prospect
        // Hand both context cards to the call screen for the in-call panel.
        myContext,
        partnerContext: payload.partner_context || null,
        partnerName,
      },
    });
  };

  const handleReady = async () => {
    readyRef.current = true;
    setIAmReady(true);
    // Immediate post so a waiting partner sees it now, not next poll tick.
    // On failure the poll loop simply carries ready: true on its next pass.
    try {
      const sentAt = Date.now();
      const res = await api.post("/matchmaking/ready", {
        room_name: payload.room,
        ready: true,
      });
      const d = res.data;
      offsetRef.current = d.server_now * 1000 + (Date.now() - sentAt) / 2 - Date.now();
      setPartnerReady(!!d.partner_ready);
      if (d.status === "cancelled") {
        setCancelReason(d.reason || "left");
        setPhase("cancelled");
      } else if (d.start_at) {
        startAtRef.current = d.start_at * 1000;
        setPhase("countdown");
      }
    } catch (err) {
      if (err?.response?.status === 404) {
        setCancelReason("expired");
        setPhase("cancelled");
      }
      /* otherwise the poll loop retries */
    }
  };

  const handleLeave = () => {
    // Fire-and-forget: cancels the countdown for the partner as well.
    if (payload?.room) {
      api
        .post("/matchmaking/ready", { room_name: payload.room, leave: true })
        .catch(() => {});
    }
    sessionStorage.removeItem(SS_MATCH);
    navigate("/homepage", { replace: true });
  };

  // No match payload (direct visit / expired storage) → back to safety.
  useEffect(() => {
    if (!payload?.room) {
      toast.error("That match is no longer available. Start a new search.");
      navigate("/homepage", { replace: true });
    }
  }, [payload, navigate]);

  // Readiness handshake poll (~1.5 s cadence, StrictMode-safe single loop).
  // Carries the explicit "I'm Ready" tap — page load alone never counts.
  useEffect(() => {
    if (!payload?.room) return undefined;
    let alive = true;
    let timer = null;
    const ping = async () => {
      try {
        const sentAt = Date.now();
        const res = await api.post("/matchmaking/ready", {
          room_name: payload.room,
          ready: readyRef.current,
        });
        if (!alive) return;
        const d = res.data;
        // RTT-aware clock sync: the server's timestamp is about half a
        // round-trip old when it arrives, so credit half the RTT instead of
        // attributing the whole thing to clock skew — both devices tick
        // against a much closer shared target.
        const rtt = Date.now() - sentAt;
        offsetRef.current = d.server_now * 1000 + rtt / 2 - Date.now();
        setPartnerReady(!!d.partner_ready);
        if (d.status === "cancelled") {
          setCancelReason(d.reason || "left");
          setPhase("cancelled");
          alive = false;
          return;
        }
        if (d.start_at) {
          startAtRef.current = d.start_at * 1000;
          setPhase("countdown");
        }
      } catch (err) {
        if (err?.response?.status === 404) {
          // Room unknown to the backend (registry expired / process
          // restarted without the durable record) — stop polling and tell
          // the user instead of waiting forever.
          setCancelReason("expired");
          setPhase("cancelled");
          alive = false;
          return;
        }
        /* transient network error — next tick retries */
      }
      if (alive) timer = setTimeout(ping, 1500);
    };
    ping();
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload?.room]);

  // Countdown tick — recomputed from the server-anchored target every
  // 250 ms so tab throttling can never drift the timer.
  useEffect(() => {
    if (phase !== "countdown") return undefined;
    const tick = () => {
      const target = startAtRef.current;
      if (!target) return;
      const remaining = target - (Date.now() + offsetRef.current);
      setSecondsLeft(Math.max(0, Math.ceil(remaining / 1000)));
      if (remaining <= 0 && !enteredRef.current) {
        enteredRef.current = true;
        handleEnter();
      }
    };
    tick();
    const t = setInterval(tick, 250);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  if (!payload?.room) return null;

  /* Role-aware cards (Step 4): the ASSIGNED prospect's scenario always
     leads — left on desktop, stacked on top on mobile — and the assigned
     rep's scenario follows. Labels track the real roles for this match.
     Legacy payloads without a role keep the original static mapping
     (partner = prospect, you = rep). Card CONTENT is untouched. */
  const myRole =
    payload.your_role === "rep" || payload.your_role === "prospect"
      ? payload.your_role
      : null;
  const iAmProspect = myRole === "prospect";
  const prospectName = myRole ? (iAmProspect ? myName : partnerName) : partnerName;
  const prospectContext = myRole
    ? iAmProspect
      ? myContext
      : payload.partner_context
    : payload.partner_context;
  const repName = myRole ? (iAmProspect ? partnerName : myName) : myName;
  const repContext = myRole
    ? iAmProspect
      ? payload.partner_context
      : myContext
    : myContext;

  return (
    <main
      className="min-h-screen bg-[#0A0A0A] px-5 pb-16 font-body text-white sm:px-8"
      data-testid="briefing-page"
    >
      <header className="mx-auto flex max-w-5xl items-center py-6">
        <button
          type="button"
          data-testid="briefing-back-btn"
          onClick={handleLeave}
          className="inline-flex items-center gap-2 text-sm text-zinc-400 transition-colors hover:text-white"
        >
          <ArrowLeft size={16} /> Leave
        </button>
      </header>

      <div className="mx-auto max-w-5xl">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="text-center"
        >
          <h1
            className="font-heading text-3xl font-black tracking-tight text-white sm:text-4xl"
            data-testid="briefing-title"
          >
            {myRole
              ? `Match found — ${repName} starts first`
              : "Match found — review the scenarios"}
          </h1>
          <p className="mx-auto mt-3 max-w-xl text-sm font-light leading-relaxed text-zinc-400">
            {myRole
              ? "Your roles have been assigned automatically. The Rep starts the conversation, and the Prospect responds. Review both scenarios, then tap I'm Ready — the countdown starts once you've both confirmed."
              : "Review both scenarios, then tap I'm Ready — the countdown starts once you've both confirmed."}
          </p>
        </motion.div>

        <div className="mt-10 grid gap-6 md:grid-cols-2">
          {/* Assigned prospect's scenario first — left on desktop, stacked
              on top on mobile; assigned rep's scenario second. Labels carry
              the actual names + starting roles for THIS round only. */}
          <ScenarioCard
            testid="prospect-scenario-card"
            title={`${prospectName} — Starts as Prospect`}
            name={prospectName}
            context={prospectContext}
            sellerLine={`${prospectName} plays the prospect this round`}
            icon={UserRound}
            tone="prospect"
          />
          <ScenarioCard
            testid="rep-scenario-card"
            title={`${repName} — Starts as Rep`}
            name={repName}
            context={repContext}
            sellerLine={
              iAmProspect
                ? `${repName} opens this call as the rep`
                : "You open this call as the rep"
            }
            icon={Briefcase}
            tone="rep"
          />
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15, duration: 0.4 }}
          className="mt-10 flex flex-col items-center gap-4"
        >
          {phase === "cancelled" ? (
            <div
              data-testid="briefing-cancelled-notice"
              className="flex flex-col items-center gap-3 rounded-3xl border border-white/10 bg-[#111111] px-8 py-6 text-center"
            >
              <p className="text-sm font-semibold text-white">
                {cancelReason === "expired"
                  ? "This match has expired."
                  : cancelReason === "gone"
                    ? `${partnerName} disconnected before the call started.`
                    : `${partnerName} left the call setup.`}
              </p>
              <p className="text-xs font-light text-zinc-500">
                No one entered the call. Head back to start a new search.
              </p>
              <button
                type="button"
                data-testid="briefing-cancelled-back-btn"
                onClick={handleLeave}
                className="mt-1 inline-flex items-center gap-2 rounded-full border border-white/15 px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:border-white/40"
              >
                Back to Homepage
              </button>
            </div>
          ) : phase === "countdown" ? (
            <>
              <p
                data-testid="briefing-countdown"
                className="font-heading text-2xl font-black tabular-nums tracking-tight text-white"
              >
                Entering the call in{" "}
                <span className="text-[#FF5A5A]">{secondsLeft ?? "…"}s</span>
              </p>
              <button
                type="button"
                data-testid="briefing-leave-bottom-btn"
                onClick={handleLeave}
                className="inline-flex items-center gap-2 rounded-full border border-white/15 px-6 py-3 text-sm font-semibold text-zinc-300 transition-colors hover:border-white/40 hover:text-white"
              >
                Leave
              </button>
            </>
          ) : (
            <>
              {iAmReady ? (
                <p
                  data-testid="briefing-waiting-partner"
                  className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-6 py-3 text-sm font-semibold text-zinc-300"
                >
                  <span className="h-2 w-2 animate-pulse rounded-full bg-[#E52525]" />
                  {partnerReady
                    ? `${partnerName} is ready — starting…`
                    : `You're ready — waiting for ${partnerName}…`}
                </p>
              ) : (
                <button
                  type="button"
                  data-testid="briefing-ready-btn"
                  onClick={handleReady}
                  className="inline-flex items-center justify-center gap-2.5 rounded-full bg-[#E52525] px-9 py-4 text-base font-semibold text-white shadow-[0_0_30px_rgba(229,37,37,0.35)] transition-[background-color,box-shadow] hover:bg-[#FF3333] hover:shadow-[0_0_45px_rgba(229,37,37,0.5)]"
                >
                  <Check size={18} />
                  I'm Ready
                </button>
              )}
              <p
                data-testid="briefing-countdown-hint"
                className="max-w-sm text-center text-xs font-light text-zinc-500"
              >
                {partnerReady && !iAmReady
                  ? `${partnerName} is ready — tap when you are.`
                  : "A 10-second countdown starts once you've both confirmed you're ready."}
              </p>
            </>
          )}
        </motion.div>
      </div>
    </main>
  );
}
